import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import type { ProfileAliasDto } from '../../services/socialService';
import type { MessageFilterDateRange, MessageFilterStatus } from '../../utils/messageFilterStore';

export interface MessageSearchFilterBarProps {
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  filterStatus: MessageFilterStatus;
  onFilterStatusChange: (value: MessageFilterStatus) => void;
  filterDateRange: MessageFilterDateRange;
  onFilterDateRangeChange: (value: MessageFilterDateRange) => void;
  filterAlias: string;
  onFilterAliasChange: (value: string) => void;
  aliases: ProfileAliasDto[];
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const MessageSearchFilterBar: React.FC<MessageSearchFilterBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  filterStatus,
  onFilterStatusChange,
  filterDateRange,
  onFilterDateRangeChange,
  filterAlias,
  onFilterAliasChange,
  aliases,
  hasActiveFilters,
  onClearFilters,
}) => {
  const aliasOptions = aliases
    .filter(a => a.bitIsActive)
    .sort((a, b) => {
      if (a.bitIsPrimary !== b.bitIsPrimary) return a.bitIsPrimary ? -1 : 1;
      return a.chrAliasName.localeCompare(b.chrAliasName);
    });

  const aliasOptionValues = new Set(aliasOptions.map(a => a.chrAliasName));
  // Persisted filter can load before alias list; keep Select value valid until options arrive.
  const showPersistedAliasOption =
    filterAlias.length > 0 && !aliasOptionValues.has(filterAlias);

  return (
    <Box sx={{ mb: 1 }}>
      {hasActiveFilters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1,
            py: 0.5,
            px: 1,
            borderRadius: 1,
            bgcolor: 'action.selected',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <FilterListIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="primary.main" fontWeight={500}>
              Filter criteria active
            </Typography>
          </Box>
          <Button size="small" onClick={onClearFilters}>
            Clear filters
          </Button>
        </Box>
      )}

      <TextField
        fullWidth
        size="small"
        placeholder="Search by subject, sender, or recipient..."
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Alias</InputLabel>
          <Select
            value={filterAlias}
            label="Alias"
            onChange={(e) => onFilterAliasChange(e.target.value)}
          >
            <MenuItem value="">All aliases</MenuItem>
            {showPersistedAliasOption && (
              <MenuItem value={filterAlias}>@{filterAlias}</MenuItem>
            )}
            {aliasOptions.map(alias => (
              <MenuItem key={alias.idAliasID} value={alias.chrAliasName}>
                @{alias.chrAliasName}
                {alias.chrDisplayName && alias.chrDisplayName !== alias.chrAliasName
                  ? ` (${alias.chrDisplayName})`
                  : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => onFilterStatusChange(e.target.value as MessageFilterStatus)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="read">Read</MenuItem>
            <MenuItem value="unread">Unread</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="authorized">Authorized</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="canceled">Canceled</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={filterDateRange}
            label="Date Range"
            onChange={(e) => onFilterDateRangeChange(e.target.value as MessageFilterDateRange)}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="week">Last 7 Days</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default MessageSearchFilterBar;
